import { TeamsChat, UserCommunication } from '@this+that/models';
import { StoreApi } from 'zustand';

import { TeamsCommunicationsState } from './types';
import { sortByLastMessage } from '../utils';

type ZustandSet = StoreApi<TeamsCommunicationsState>['setState'];
type ZustandGet = StoreApi<TeamsCommunicationsState>['getState'];

const updateChatCommunications = (
  chats: TeamsChat[],
  chatId: string,
  updater: (comms: UserCommunication[]) => UserCommunication[],
): TeamsChat[] =>
  chats.map((chat) =>
    chat.chatId === chatId
      ? { ...chat, communications: updater(chat.communications) }
      : chat,
  );

export const handleSetLoading = (set: ZustandSet, isLoading: boolean) => {
  set({ isLoading });
};

export const handleSaveTeamsChats = (
  set: ZustandSet,
  incomingChats: Partial<TeamsChat>[],
) => {
  set((state) => {
    const normalizedChats = incomingChats.map((item) => {
      const previous = state.chats.find((e) => e.chatId === item.chatId);

      return {
        ...previous,
        ...item,
        communications: previous?.communications ?? [],
      } as TeamsChat;
    });

    return {
      chats: [...normalizedChats].sort(sortByLastMessage),
    };
  });
};

// Backfills the tag so an untagged chat is reachable by clearTeamsChatsByHomeAccountId.
const homeAccountIdPatch = (
  currentHomeAccountId: string | undefined,
  incomingHomeAccountId: string | undefined,
): Partial<Pick<TeamsChat, 'homeAccountId'>> =>
  !currentHomeAccountId && incomingHomeAccountId
    ? { homeAccountId: incomingHomeAccountId }
    : {};

export const handleAddTeamsCommunication = (
  set: ZustandSet,
  get: ZustandGet,
  chatId: string,
  communication: UserCommunication,
  isWebSocket: boolean = false,
) => {
  const state = get();
  const { chats } = state;

  if (isWebSocket) {
    const isOwnMessage =
      communication.userId === communication.authorUserId ||
      communication.userEmailAddress === communication.authorEmail;
    if (isOwnMessage) {
      return;
    }
  }

  const chatExists = chats.some((chat) => chat.chatId === chatId);

  if (chatExists) {
    const updatedChats = chats.map((chat) =>
      chat.chatId === chatId
        ? {
            ...chat,
            ...homeAccountIdPatch(
              chat.homeAccountId,
              communication.teamsHomeAccountId,
            ),
            communications: [communication, ...chat.communications],
            lastMessage: communication,
          }
        : chat,
    );
    set({
      chats: updatedChats.sort(sortByLastMessage),
    });
  } else {
    const newChat: TeamsChat = {
      chatId,
      chatType: 'oneOnOne',
      topic: communication.channelName || chatId,
      userId: communication.userId || '',
      tenantId: '',
      refreshedAt: Date.now(),
      communications: [communication],
      lastMessage: communication,
      members: [],
      ...homeAccountIdPatch(undefined, communication.teamsHomeAccountId),
    };

    const updatedChats = [newChat, ...chats];
    set({
      chats: updatedChats.sort(sortByLastMessage),
    });
  }
};

export const handleGetTeamsCommunicationById = (
  get: ZustandGet,
  chatId: string,
  messageId: string,
): UserCommunication | undefined => {
  const state = get();
  return state.chats
    .find((chat) => chat.chatId === chatId)
    ?.communications.find((comm) => comm.messageId === messageId);
};

export const handleUpdateTeamsCommunication = (
  set: ZustandSet,
  get: ZustandGet,
  chatId: string,
  messageId: string,
  updatedFields: Partial<UserCommunication>,
) => {
  const state = get();
  const updatedChats = updateChatCommunications(state.chats, chatId, (comms) =>
    comms.map((comm) =>
      comm.messageId === messageId ? { ...comm, ...updatedFields } : comm,
    ),
  );

  const chatsWithUpdatedLastMessage = updatedChats.map((chat) => {
    if (chat.chatId === chatId && chat.communications.length > 0) {
      const latestMessage = chat.communications[0];
      if (latestMessage.messageId === messageId) {
        return {
          ...chat,
          lastMessage: { ...latestMessage, ...updatedFields },
        };
      }
    }
    return chat;
  });

  set({
    chats: chatsWithUpdatedLastMessage.sort(sortByLastMessage),
  });
};

export const handleDeleteTeamsCommunication = (
  set: ZustandSet,
  get: ZustandGet,
  chatId: string,
  messageId: string,
) => {
  const state = get();
  const updatedChats = updateChatCommunications(state.chats, chatId, (comms) =>
    comms.filter((comm) => comm.messageId !== messageId),
  );

  const chatsWithUpdatedLastMessage = updatedChats.map((chat) => {
    if (chat.chatId === chatId) {
      const newLastMessage =
        chat.communications.length > 0 ? chat.communications[0] : undefined;
      return { ...chat, lastMessage: newLastMessage };
    }
    return chat;
  });

  set({
    chats: chatsWithUpdatedLastMessage.sort(sortByLastMessage),
  });
};

export const handleDeleteTeamsCommunications = (
  set: ZustandSet,
  get: ZustandGet,
  chatId: string,
  messageIds: string[],
) => {
  const state = get();
  const chat = state.chats.find((c) => c.chatId === chatId);

  if (!chat) {
    return;
  }

  const heldIds = new Set(
    messageIds.filter((messageId) =>
      chat.communications.some((comm) => comm.messageId === messageId),
    ),
  );

  if (heldIds.size === 0) {
    return;
  }

  const updatedChats = updateChatCommunications(state.chats, chatId, (comms) =>
    comms.filter((comm) => !heldIds.has(comm.messageId)),
  );

  const chatsWithUpdatedLastMessage = updatedChats.map((updatedChat) => {
    if (updatedChat.chatId === chatId) {
      const newLastMessage =
        updatedChat.communications.length > 0
          ? updatedChat.communications[0]
          : undefined;
      return { ...updatedChat, lastMessage: newLastMessage };
    }
    return updatedChat;
  });

  set({
    chats: chatsWithUpdatedLastMessage.sort(sortByLastMessage),
  });
};

export const handleSetChatCommunications = (
  set: ZustandSet,
  get: ZustandGet,
  chatId: string,
  communications: UserCommunication[],
) => {
  const state = get();
  const updatedChats = updateChatCommunications(
    state.chats,
    chatId,
    () => communications,
  );

  const chatsWithUpdatedLastMessage = updatedChats.map((chat) => {
    if (chat.chatId === chatId) {
      const newLastMessage =
        communications.length > 0 ? communications[0] : undefined;
      return { ...chat, lastMessage: newLastMessage };
    }
    return chat;
  });

  set({
    chats: chatsWithUpdatedLastMessage.sort(sortByLastMessage),
  });
};

export const handleClearTeamsCommunicationsByTenantId = (
  set: ZustandSet,
  tenantId: string,
) => {
  set((state) => ({
    chats: state.chats.filter((chat) => chat.tenantId !== tenantId),
  }));
};

export const handleClearTeamsChatsByHomeAccountId = (
  set: ZustandSet,
  homeAccountId: string,
) => {
  set((state) => ({
    chats: state.chats.filter((chat) => chat.homeAccountId !== homeAccountId),
  }));
};
